// src/pages/Login.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const CUBE_FACES = [
  'translateZ(40px)', 'rotateY(180deg) translateZ(40px)',
  'rotateY(90deg) translateZ(40px)', 'rotateY(-90deg) translateZ(40px)',
  'rotateX(90deg) translateZ(40px)', 'rotateX(-90deg) translateZ(40px)',
];

export default function Login({ onBack }) {
  const { login, verifyOtp } = useAuth();

  // step: 'email' | 'otp'
  const [step, setStep]       = useState('email');
  const [userId, setUserId]   = useState(null);
  const [otpHint, setOtpHint] = useState('');

  const [email, setEmail]       = useState('');
  const [code, setCode]         = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleEmail = async () => {
    if (!email) return setError('Enter your email address.');
    setLoading(true); setError('');
    try {
      const res = await login(email);
      if (res.requiresOtp) {
        setUserId(res.userId);
        setOtpHint(res.message || 'Check your email for the 6-digit code.');
        setStep('otp');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Try again.');
    } finally { setLoading(false); }
  };

  const handleOtp = async () => {
    if (!code || code.length !== 6) return setError('Enter the 6-digit code from your email.');
    setLoading(true); setError('');
    try {
      await verifyOtp(userId, code);
      // AuthContext sets user → App re-renders to dashboard automatically
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code.');
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    setError(''); setCode('');
    setLoading(true);
    try {
      const res = await login(email);
      setOtpHint(res.message || 'A new code has been sent.');
    } catch (err) {
      setError('Could not resend code. Please start over.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight:'100vh', display:'flex', justifyContent:'center', alignItems:'center',
      padding:'20px', fontFamily:'Arial, sans-serif',
      background:'radial-gradient(circle at 40% 40%, #0b1f3a, #050b18)',
    }}>
      <style>{`
        @keyframes spin { 0%{transform:rotateY(0deg) rotateX(10deg)} 100%{transform:rotateY(360deg) rotateX(10deg)} }
        .ms-cube { animation: spin 4s infinite linear; }
        .ms-inp {
          width:100%; padding:12px 14px; margin:6px 0; border-radius:8px; font-size:14px;
          background:rgba(255,255,255,0.07); color:#fff; outline:none; box-sizing:border-box;
          border:1px solid rgba(0,212,255,0.2); transition:border-color .2s;
        }
        .ms-inp:focus { border-color:#00d4ff; }
        .ms-inp::placeholder { color:rgba(255,255,255,0.35); }
        .ms-btn {
          width:100%; padding:13px; margin-top:12px; border:none; border-radius:8px;
          background:linear-gradient(135deg,#1e88e5,#00d4ff); color:#0b1f3a;
          font-weight:800; font-size:15px; cursor:pointer; transition:opacity .2s, transform .1s;
          letter-spacing:0.03em;
        }
        .ms-btn:hover:not(:disabled) { opacity:0.88; }
        .ms-btn:active:not(:disabled) { transform:scale(0.99); }
        .ms-btn:disabled { opacity:0.45; cursor:not-allowed; }
        .otp-input {
          letter-spacing:14px; text-align:center; font-size:26px; font-weight:800;
          font-family:'Courier New', monospace;
        }
      `}</style>

      <div style={{
        width:'100%', maxWidth:'340px', padding:'38px 30px',
        background:'rgba(11,31,58,0.85)', backdropFilter:'blur(14px)',
        borderRadius:'18px', textAlign:'center', color:'#fff',
        boxShadow:'0 0 60px rgba(0,212,255,0.12), 0 24px 64px rgba(0,0,0,0.6)',
        border:'1px solid rgba(0,212,255,0.13)',
      }}>
        {/* 3-D spinning cube */}
        <div style={{ perspective:'800px', marginBottom:'22px' }}>
          <div className="ms-cube" style={{ width:'78px', height:'78px', margin:'auto', position:'relative', transformStyle:'preserve-3d' }}>
            {CUBE_FACES.map((t, i) => (
              <div key={i} style={{
                position:'absolute', width:'78px', height:'78px',
                background:'linear-gradient(135deg,#1e88e5,#00d4ff)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'21px', fontWeight:'900', color:'#fff',
                border:'1px solid rgba(255,255,255,0.18)', transform: t,
              }}>MS</div>
            ))}
          </div>
        </div>

        <h2 style={{ margin:'0 0 4px', fontSize:'22px', fontWeight:900, letterSpacing:'0.06em' }}>MASTER SOLVER</h2>
        <p style={{ margin:'0 0 28px', color:'#00d4ff', fontSize:'12px', letterSpacing:'0.04em' }}>Solve Today, Empower Tomorrow</p>

        {onBack && step === 'email' && (
          <button onClick={onBack} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer', fontSize:'12px', padding:0, marginBottom:'18px', display:'block' }}>
            ← Back to website
          </button>
        )}

        {step === 'email' ? (
          <>
            <div style={{ textAlign:'left', marginBottom:'4px', fontSize:'11px', color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Step 1 of 2 — Your email</div>
            <input className="ms-inp" type="email" placeholder="Email address" value={email}
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              onPaste={e => e.preventDefault()}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleEmail()} />
            {error && <ErrorBox msg={error} />}
            <button className="ms-btn" onClick={handleEmail} disabled={loading}>
              {loading ? 'Checking...' : 'Send Code →'}
            </button>
          </>
        ) : (
          <>
            <div style={{ textAlign:'left', marginBottom:'4px', fontSize:'11px', color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Step 2 of 2 — Verification code</div>
            <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.6)', marginBottom:'16px', textAlign:'left', lineHeight:1.5 }}>
              {otpHint}
            </div>
            <input className="ms-inp otp-input" type="text" inputMode="numeric" pattern="[0-9]*"
              maxLength={6} placeholder="000000" value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
              onKeyDown={e => e.key === 'Enter' && handleOtp()} />
            {error && <ErrorBox msg={error} />}
            <button className="ms-btn" onClick={handleOtp} disabled={loading || code.length !== 6}>
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
            <div style={{ marginTop:'16px', display:'flex', justifyContent:'space-between', fontSize:'12px' }}>
              <button onClick={() => { setStep('email'); setCode(''); setError(''); }}
                style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer', padding:0 }}>
                ← Back
              </button>
              <button onClick={resendCode} disabled={loading}
                style={{ background:'none', border:'none', color:'#00d4ff', cursor:'pointer', padding:0 }}>
                Resend code
              </button>
            </div>
          </>
        )}

        <p style={{ marginTop:'24px', fontSize:'11px', color:'rgba(255,255,255,0.22)' }}>
          Master Solver ERP · Secure Staff Portal
        </p>
      </div>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{ color:'#ff6b6b', fontSize:'12.5px', margin:'8px 0 4px', textAlign:'left', padding:'9px 12px', background:'rgba(255,107,107,0.1)', borderRadius:'7px', border:'1px solid rgba(255,107,107,0.25)' }}>
      {msg}
    </div>
  );
}
